import { forwardRef, useMemo } from "react";
import { type Column } from "@racoon/ui";
import { useT, Namespace, TranslationKey } from "@racoon/i18n";
import {
  PickupLocationRegistryRow,
  usePickupLocationRegistryRows,
} from "../model/use-schedule-report-rows";
import { linkedList, useReportHyperlinks } from "../model/use-report-hyperlinks";
import { compareIds, entityNumber, numberColumn } from "../model/job-report";
import { ReportTable, ReportTableHandle, ReportTableProps } from "./report-table";

/** «Реестр площадок» (AIS 15.3.1): each site with its customers, containers and schedules. */
export const PickupLocationRegistryReportTable = forwardRef<ReportTableHandle, ReportTableProps>(
  (props, ref) => {
    const t = useT();
    const { rows, isLoading, error, schedulesFailed } = usePickupLocationRegistryRows();
    const { locationCell, customerCell, entityCards } = useReportHyperlinks();

    const columns = useMemo(() => {
      const all: Column<PickupLocationRegistryRow>[] = [
        numberColumn(t(Namespace.Analytics, "columns.number")),
        {
          id: "id",
          header: t(Namespace.Analytics, "columns.id"),
          accessor: (row) => entityNumber("site", row.pickupLocationId),
          width: 100,
          sortable: true,
          sortFn: (a, b) => compareIds(a.pickupLocationId, b.pickupLocationId),
        },
        {
          id: "client",
          header: t(Namespace.Analytics, "table.customer"),
          accessor: (row) => row.ownerOrganizations.map((client) => client.name).join(", ") || "—",
          width: 220,
          sortable: true,
          cell: (info) => linkedList(info.row.original.ownerOrganizations, customerCell),
        },
        {
          id: "site",
          header: t(Namespace.Analytics, "columns.site"),
          accessor: (row) => row.pickupLocationName || "—",
          width: 200,
          sortable: true,
          cell: (info) =>
            locationCell(info.row.original.pickupLocationId, info.row.original.pickupLocationName),
        },
        {
          id: "wasteType",
          header: t(Namespace.Analytics, "schedules.wasteType"),
          accessor: (row) =>
            row.jobCategories.map((key) => t(Namespace.Enum, key as TranslationKey)).join(", ") ||
            "—",
          width: 150,
          sortable: true,
        },
        {
          id: "containers",
          header: t(Namespace.Analytics, "siteRegistry.containers"),
          accessor: (row) => row.containers || "—",
          width: 240,
          sortable: true,
        },
        {
          id: "schedules",
          header: t(Namespace.Analytics, "siteRegistry.schedules"),
          accessor: (row) => row.schedules || "—",
          width: 360,
          sortable: true,
        },
      ];
      // Waste type and schedules come from the schedule rows: without them the columns go.
      return schedulesFailed
        ? all.filter((column) => column.id !== "wasteType" && column.id !== "schedules")
        : all;
    }, [t, locationCell, customerCell, schedulesFailed]);

    return (
      <ReportTable
        {...props}
        exportRef={ref}
        settingsKey="site_registry_report"
        reportName={t(Namespace.Analytics, "reports.siteRegistry")}
        columns={columns}
        rows={rows}
        rowKey={(row) => String(row.pickupLocationId)}
        isLoading={isLoading}
        error={error}
      >
        {entityCards}
      </ReportTable>
    );
  },
);
